import { useEffect } from 'preact/hooks'
import type { GalleryItem } from '~/types'
import { media, sized } from '~/utils/media'
import FullsizeImage from './FullsizeImage'

interface LightboxProps {
	items: GalleryItem[]
	index: number
	onClose: () => void
	onIndexChange: (index: number) => void
}

const pad = (n: number): string => String(n).padStart(2, '0')

// Full-viewport image viewer rendered INLINE (a plain fixed overlay, not a portaled
// MUI Modal) so it stays inside the project modal's DOM — clicking its backdrop closes
// only the image, never the surrounding ProjectModal/ClickAwayListener. Provides its
// own close button, prev/next controls, a position counter, and keyboard nav.
export default function Lightbox({ items, index, onClose, onIndexChange }: LightboxProps) {
	const count = items.length
	const item = items[index]

	useEffect(() => {
		const go = (delta: number) => onIndexChange((index + delta + count) % count)
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				// Swallow so the parent ProjectModal's Esc handler doesn't also fire.
				e.stopPropagation()
				e.preventDefault()
				onClose()
			} else if (e.key === 'ArrowLeft' && count > 1) {
				e.stopPropagation()
				go(-1)
			} else if (e.key === 'ArrowRight' && count > 1) {
				e.stopPropagation()
				go(1)
			}
		}
		window.addEventListener('keydown', onKey, true)
		return () => window.removeEventListener('keydown', onKey, true)
	}, [index, count, onClose, onIndexChange])

	if (!item) return null

	const stop = (e: Event) => e.stopPropagation()
	const go = (delta: number) => onIndexChange((index + delta + count) % count)
	const arrow =
		'absolute top-1/2 -translate-y-1/2 z-10 flex h-12 w-12 items-center justify-center border border-primary/30 bg-void/[0.45] text-3xl leading-none text-primary transition hover:border-primary hover:text-primary-light hover:shadow-glow-green'

	return (
		<div className="fixed inset-0 z-50 flex flex-col bg-void" onClick={onClose}>
			{/* Top bar: position counter + close */}
			<div className="flex items-center justify-between p-4" onClick={stop}>
				<span className="text-xs tracking-[0.4em] text-primary opacity-70 [font-family:monospace]">
					{pad(index + 1)} / {pad(count)}
				</span>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close image"
					className="flex h-9 w-9 items-center justify-center border-none bg-transparent text-2xl leading-none text-white/70 cursor-pointer hover:text-white"
				>
					×
				</button>
			</div>

			{/* Center: prev / image / next */}
			<div
				className="relative flex flex-1 items-center justify-center overflow-hidden px-4"
				onClick={onClose}
			>
				{count > 1 && (
					<button
						type="button"
						aria-label="Previous image"
						className={`${arrow} left-3`}
						onClick={(e) => {
							stop(e)
							go(-1)
						}}
					>
						‹
					</button>
				)}

				<div onClick={stop} className="flex flex-col items-center">
					<FullsizeImage
						key={item.src}
						src={sized(item.src, 2000, { fit: 'contain', q: 74 })}
						alt={item.alt}
					/>
				</div>

				{count > 1 && (
					<button
						type="button"
						aria-label="Next image"
						className={`${arrow} right-3`}
						onClick={(e) => {
							stop(e)
							go(1)
						}}
					>
						›
					</button>
				)}
			</div>

			{/* Bottom: caption + full-resolution link */}
			<div
				className="flex items-center justify-center gap-4 p-4 text-sm text-white/[0.85]"
				onClick={stop}
			>
				{item.caption && <span>{item.caption}</span>}
				<a
					href={item.full ?? media(item.src)}
					target="_blank"
					rel="noreferrer noopener"
					className="text-primary-light no-underline hover:underline"
				>
					View full resolution ↗
				</a>
			</div>
		</div>
	)
}
